/**
 * `/$org/skills/$slug`: one skill, as a skills.sh skill page — its name,
 * source and owner, what it is for, how to use it in chat (the agent's
 * `$name` token, where skills.sh shows the install command), its loads over
 * the last eight weeks, and its SKILL.md rendered.
 *
 * The board (state/skillBoard) names the skill; its method comes from the
 * library's details read (state/skillDetails) for a library skill, or from the
 * SKILL.md the agent ships for one of the agent's own (agentSkillBodies).
 * "Use in chat" opens the organization's new chat with the skill in its
 * composer (hooks/useStartChatWithText); "Edit in chat" (an admin, on the
 * organization's own skill) drafts the change with the agent, as "Create
 * skill" does.
 *
 * @module components/skills/SkillDetailPage
 */
import type { SkillDetails } from "@amoreg/contracts";
import { Link } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CheckIcon,
  CopyIcon,
  MessageSquarePlusIcon,
  PencilIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

import ChatMarkdown from "../ChatMarkdown";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { useStartChatWithText } from "../../hooks/useStartChatWithText";
import { cn } from "../../lib/utils";
import { orgUrlSegment } from "../../orgRouting";
import { useActiveOrg } from "../../state/orgs";
import { useSkillBoard, type SkillBoardEntry } from "../../state/skillBoard";
import { requestSkillDetails, SkillRequestError } from "../../state/skillDetails";
import { SKILL_SOURCE_LABEL } from "../connections/skillContent";
import { SkillTile } from "../connections/SkillRow";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { loadAgentSkillBody } from "./agentSkillBodies";
import {
  editInChatPrompt,
  findSkillBySlug,
  formatUses,
  skillChatPrompt,
  skillOwner,
  skillSource,
} from "./skillBoardLogic";
import { SkillSparkline } from "./SkillSparkline";

type BodyState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly body: string; readonly details: SkillDetails | null }
  | { readonly kind: "missing"; readonly message: string };

/** The skill's method: the library's details read, or the agent's own SKILL.md. */
function useSkillBody(organizationId: string | null, entry: SkillBoardEntry | null): BodyState {
  const [state, setState] = useState<{ readonly key: string | null; readonly body: BodyState }>({
    key: null,
    body: { kind: "loading" },
  });
  const key = entry && organizationId ? `${organizationId}:${entry.key}` : null;
  useEffect(() => {
    if (!entry || !organizationId || key === null) return;
    const controller = new AbortController();
    const settle = (body: BodyState) => {
      if (!controller.signal.aborted) setState({ key, body });
    };
    if (entry.kind === "agent") {
      void loadAgentSkillBody(entry.slug).then((body) =>
        settle(
          body
            ? { kind: "ready", body, details: null }
            : { kind: "missing", message: "This skill’s method ships with the Amoreg agent." },
        ),
      );
    } else if (entry.id) {
      requestSkillDetails(organizationId, entry.id, controller.signal)
        .then((details) => settle({ kind: "ready", body: details.body, details }))
        .catch((cause: unknown) =>
          settle({
            kind: "missing",
            message:
              cause instanceof SkillRequestError
                ? cause.message
                : "Could not load this skill. Try again.",
          }),
        );
    }
    return () => controller.abort();
  }, [entry, key, organizationId]);
  return state.key === key ? state.body : { kind: "loading" };
}

function UsageToken({ token }: { readonly token: string }) {
  const { copyToClipboard, isCopied } = useCopyToClipboard();
  return (
    <div className="flex h-10 items-center gap-2 rounded-xl border border-border/60 bg-muted/40 ps-3 pe-1.5 font-mono text-[13px]">
      <span aria-hidden className="select-none text-muted-foreground/70">
        ›
      </span>
      <span className="min-w-0 flex-1 truncate text-foreground">{token}</span>
      <Button
        size="icon-xs"
        variant="ghost-muted"
        aria-label={isCopied ? "Copied" : `Copy ${token}`}
        onClick={() => copyToClipboard(token, undefined)}
      >
        {isCopied ? <CheckIcon className="size-3.5" /> : <CopyIcon className="size-3.5" />}
      </Button>
    </div>
  );
}

function Fact({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5 text-[13px]">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-right text-foreground">{children}</dd>
    </div>
  );
}

export function SkillDetailPage({ slug }: { readonly slug: string }) {
  const org = useActiveOrg();
  const orgId = org?.id ?? null;
  const orgSegment = orgId ? orgUrlSegment(orgId) : null;
  const startChat = useStartChatWithText(orgId);
  const { result } = useSkillBoard(orgId);
  const board = result?.kind === "ok" ? result.board : null;
  const entry = board ? findSkillBySlug(board.skills, slug) : null;
  const body = useSkillBody(orgId, entry);

  const openChatWith = (text: string) => {
    if (startChat) void startChat(text);
  };

  const back = orgSegment ? (
    <Link
      to="/$org/skills"
      params={{ org: orgSegment }}
      className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeftIcon aria-hidden className="size-3.5" />
      Skills
    </Link>
  ) : null;

  const frame = (children: React.ReactNode) => (
    <ScrollArea className="h-full">
      <div
        data-skill-page={slug}
        className="@container mx-auto flex w-full max-w-[1240px] flex-col gap-6 px-4 pb-16 pt-14 sm:px-8 sm:pt-8"
      >
        {back}
        {children}
      </div>
    </ScrollArea>
  );

  if (result === null) {
    return frame(
      <div role="status" aria-label="Loading skill" className="flex flex-col gap-3">
        <div className="h-9 w-72 animate-pulse rounded-lg bg-muted/60" />
        <div className="h-4 w-full max-w-xl animate-pulse rounded bg-muted/50" />
        <div className="mt-4 h-64 animate-pulse rounded-2xl bg-muted/40" />
      </div>,
    );
  }
  if (!entry) {
    return frame(
      <section className="rounded-2xl border border-border/60 bg-card px-6 py-8">
        <h1 className="text-[17px] font-medium text-foreground">
          {result.kind === "ok" ? "This skill isn’t available here" : "The skill didn’t load"}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {result.kind === "ok"
            ? `No skill called “${slug}” is available to ${org?.name ?? "this organisation"}.`
            : "Try again in a moment."}
        </p>
      </section>,
    );
  }

  const source = skillSource(entry);
  const owner = skillOwner(entry, org?.name);
  const methodBody = body.kind === "ready" ? body.body : null;
  const canEdit = body.kind === "ready" && body.details?.canEdit === true;
  const updatedAt = body.kind === "ready" ? (body.details?.updatedAt ?? null) : null;

  return frame(
    <>
      <header className="flex flex-col gap-5">
        <div className="flex items-start gap-4">
          <SkillTile
            name={entry.name}
            category={entry.category}
            className="mt-0.5 size-12 rounded-2xl"
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-balance text-[28px] font-semibold leading-tight tracking-[-0.02em] text-foreground">
              {entry.name}
            </h1>
            <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-muted-foreground">
              <span className="font-mono text-[12px]">
                {owner}/{entry.slug}
              </span>
              <span aria-hidden>·</span>
              <span
                className={cn(
                  "inline-flex h-5 items-center rounded-full border px-2 text-[11px] font-medium",
                  source === "system"
                    ? "border-border/70 text-muted-foreground"
                    : "border-primary/25 bg-primary/8 text-primary",
                )}
                data-skill-source={source}
              >
                {SKILL_SOURCE_LABEL[source]}
              </span>
              {entry.category ? (
                <>
                  <span aria-hidden>·</span>
                  <span>{entry.category}</span>
                </>
              ) : null}
            </p>
          </div>
        </div>
        <p className="max-w-3xl text-[15px] leading-7 text-foreground/85">{entry.description}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            className="gap-1.5"
            data-skill-use=""
            disabled={startChat === null || (entry.kind === "library" && body.kind === "loading")}
            onClick={() => openChatWith(skillChatPrompt(entry, methodBody))}
          >
            <MessageSquarePlusIcon aria-hidden className="size-3.5" />
            Use in chat
          </Button>
          {canEdit ? (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              data-skill-edit=""
              onClick={() => openChatWith(editInChatPrompt(entry, methodBody))}
            >
              <PencilIcon aria-hidden className="size-3.5" />
              Edit in chat
            </Button>
          ) : null}
        </div>
      </header>

      <div className="grid gap-8 @[56rem]:grid-cols-[minmax(0,1fr)_18rem]">
        <article
          aria-label="SKILL.md"
          className="min-w-0 overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
        >
          <div className="flex h-10 items-center gap-2 border-b border-border/60 px-5 font-mono text-[12px] text-muted-foreground">
            SKILL.md
          </div>
          <div className="px-5 py-5 sm:px-7 sm:py-6">
            {body.kind === "loading" ? (
              <div role="status" aria-label="Loading the method" className="flex flex-col gap-2.5">
                {[92, 80, 86, 60, 74].map((width) => (
                  <div
                    key={width}
                    className="h-3.5 animate-pulse rounded bg-muted/60"
                    style={{ width: `${width}%` }}
                  />
                ))}
              </div>
            ) : body.kind === "missing" ? (
              <p className="text-sm text-muted-foreground">{body.message}</p>
            ) : (
              <ChatMarkdown text={body.body} cwd={undefined} headingLevelOffset={1} />
            )}
          </div>
        </article>

        <aside className="order-first flex flex-col gap-6 @[56rem]:sticky @[56rem]:top-8 @[56rem]:order-none @[56rem]:self-start">
          {entry.kind === "agent" ? (
            <section>
              <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
                Use in chat
              </h2>
              <UsageToken token={`$${entry.slug}`} />
              <p className="mt-2 text-[12px] leading-5 text-muted-foreground">
                Type it in the composer, or pick it from the <span className="font-mono">$</span>{" "}
                menu.
              </p>
            </section>
          ) : null}
          <section>
            <h2 className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
              Uses · last 8 weeks
            </h2>
            <div className="flex items-end justify-between gap-4">
              <span className="text-[32px] font-medium leading-none tracking-[-0.03em] tabular-nums text-foreground">
                {formatUses(entry.uses)}
              </span>
              <SkillSparkline values={entry.weekly} width={132} height={36} />
            </div>
          </section>
          <dl className="divide-y divide-border/60 border-y border-border/60">
            <Fact label="Owner">{owner}</Fact>
            <Fact label="Source">{SKILL_SOURCE_LABEL[source]}</Fact>
            {entry.category ? <Fact label="Topic">{entry.category}</Fact> : null}
            {entry.addedAt ? (
              <Fact label="Added">
                {new Date(entry.addedAt).toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </Fact>
            ) : null}
            {updatedAt ? (
              <Fact label="Updated">
                {new Date(updatedAt).toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </Fact>
            ) : null}
            {body.kind === "ready" && body.details ? (
              <Fact label="Version">{body.details.version}</Fact>
            ) : null}
          </dl>
        </aside>
      </div>
    </>,
  );
}
